import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import type { AdminAuthUser, LoginForm } from '../types/admin';

interface AuthContextValue {
  user: AdminAuthUser | null;
  loading: boolean;
  login: (form: LoginForm) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminAuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get<AdminAuthUser>('/auth/me');
      console.log('[AuthProvider] /auth/me SUCCESS, data keys:', Object.keys(data as object), 'data:', data);
      setUser(data);
    } catch (err) {
      console.log('[AuthProvider] /auth/me FAILED, err:', err, 'will setUser(null)');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (form: LoginForm) => {
    await api.post('/auth/login', form);
    await fetchMe();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
